import { useEffect, useState } from "react";
import { Download, Trash2, Eye, Phone, Mail, MapPin, Package, IndianRupee } from "lucide-react";
import { toast } from "sonner";
import { api, BACKEND_URL } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUSES = ["new", "contacted", "quoted", "confirmed", "shipped", "delivered", "cancelled"];
const STATUS_COLORS = {
  new: "bg-[#E65C00] text-white",
  contacted: "bg-blue-100 text-blue-800",
  quoted: "bg-yellow-100 text-yellow-800",
  confirmed: "bg-green-100 text-green-800",
  shipped: "bg-purple-100 text-purple-800",
  delivered: "bg-gray-200 text-gray-800",
  cancelled: "bg-red-100 text-red-800",
};

export default function AdminSpareOrders() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/spare-orders").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this order?")) return;
    await api.delete(`/admin/spare-orders/${id}`);
    toast.success("Deleted");
    load();
  };

  const setStatus = async (id, status) => {
    try {
      await api.put(`/admin/spare-orders/${id}/status?status=${status}`);
      toast.success(`Status: ${status}`);
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const exportCsv = async () => {
    const token = localStorage.getItem("tara_token");
    const res = await fetch(`${BACKEND_URL}/api/admin/spare-orders/export`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { toast.error("Export failed"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `spare-orders-${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const fmt = (d) => { try { return new Date(d).toLocaleString("en-IN", { hour12: true }); } catch { return d; } };

  return (
    <div data-testid="admin-spare-orders">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="heading-xl text-3xl">SPARE ORDERS</h1>
        <button onClick={exportCsv} className="btn-primary text-sm"><Download className="w-4 h-4" /> Export CSV</button>
      </div>

      <div className="bg-white border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              {["Date", "Status", "Spare", "Qty", "Customer", "Phone", "City"].map((c) => (
                <th key={c} className="text-left px-4 py-3 text-[10px] uppercase tracking-widest">{c}</th>
              ))}
              <th className="w-24"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan="8" className="px-4 py-8 text-center text-gray-500">Loading...</td></tr>
              : items.length === 0 ? <tr><td colSpan="8" className="px-4 py-8 text-center text-gray-500">No orders yet.</td></tr>
              : items.map((o) => (
                <tr key={o.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`order-row-${o.id}`}>
                  <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{fmt(o.created_at)}</td>
                  <td className="px-4 py-3">
                    <select value={o.status} onChange={(e) => setStatus(o.id, e.target.value)} className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 border-0 cursor-pointer ${STATUS_COLORS[o.status] || ""}`}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 max-w-[200px] truncate font-semibold">{o.spare_title}</td>
                  <td className="px-4 py-3 font-bold">{o.quantity}</td>
                  <td className="px-4 py-3">{o.name}</td>
                  <td className="px-4 py-3">{o.phone}</td>
                  <td className="px-4 py-3">{o.city}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => setViewing(o)} className="p-1.5 hover:bg-gray-100" aria-label="View" data-testid={`order-view-${o.id}`}><Eye className="w-4 h-4" /></button>
                    <button onClick={() => remove(o.id)} className="p-1.5 hover:bg-red-50 text-red-600 ml-1" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!viewing} onOpenChange={(o) => { if (!o) setViewing(null); }}>
        <DialogContent className="max-w-xl" data-testid="order-detail">
          {viewing && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading font-black uppercase">
                  Order Details
                  <span className={`ml-3 text-[10px] uppercase tracking-widest font-bold px-2 py-1 ${STATUS_COLORS[viewing.status] || ""}`}>{viewing.status}</span>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2 text-sm">
                <div className="bg-gray-50 p-4 border-l-4 border-[#E65C00]">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gray-500 font-bold"><Package className="w-3.5 h-3.5" /> Spare</div>
                  <div className="font-heading font-bold mt-1">{viewing.spare_title}</div>
                  <div className="text-xs text-gray-600">Quantity: <strong>{viewing.quantity}</strong></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Detail label="Name" value={viewing.name} />
                  <Detail label="Email" value={viewing.email} icon={Mail} />
                  <Detail label="Phone" value={viewing.phone} icon={Phone} />
                  <Detail label="Company" value={viewing.company || "—"} />
                  <Detail label="GST" value={viewing.gst_number || "—"} icon={IndianRupee} />
                  <Detail label="Pincode" value={viewing.pincode} />
                </div>
                <Detail label="Address" value={viewing.address} icon={MapPin} />
                <div className="grid grid-cols-2 gap-3">
                  <Detail label="City" value={viewing.city} />
                  <Detail label="State" value={viewing.state} />
                </div>
                {viewing.message && <Detail label="Customer Notes" value={viewing.message} />}
                <div className="text-xs text-gray-400 pt-3 border-t">Order ID: {viewing.id} · Placed: {fmt(viewing.created_at)}</div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Detail({ label, value, icon: Icon }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className="mt-1 font-semibold">{value}</div>
    </div>
  );
}
